/* eslint-disable no-unused-vars */
/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, {
  useEffect,
  useMemo,
  UseContext,
  useState,
  useContext,
  useCallback,
} from 'react'
import {
  Content,
  CriteriaRow,
  Filler,
  FullHeightSizer,
  Title,
} from 'core/screen-util'
import { Button, Avatar, Popconfirm, Popover, Input, Icon, message } from 'antd'

import R, { sortWith, ascend } from 'ramda'
import RoleModal from './RoleModal'
import {
  getApiTargetInfo,
  getReportNameMap,
  getUtilityStationMap,
  getCompanyNameMap,
  getCompanyUtilityMap,
  getUtilityUnitsMap,
  getStationUtilityMap,
  getUnits,
  getStations,
  getUnitGroups,
  getEnvConfig,
  hasAccesPermits,
  getPermitActions,
} from 'core'
import { connect } from 'react-redux'
import {
  getRoles,
  saveRole,
  deleteRole,
  getAllUnitInfo,
  getAllActions,
  transformUserGroupToRole,
  getUsers,
} from 'api/pgads'
import { SpinContext, ErrorContext } from 'context'
import RenameModal from './RenameModal'
import { List } from 'immutable-ext'
import Task from 'data.task'
import {
  RowButtons,
  RowContainer,
  RowTitle,
} from 'core/component/GenericEditScreen'
import { updateRow } from '@integec/grid-tools/lib/editEngine'
import * as ScreenPermissions from 'core/EventActions'
import UserTree from './UserTree'
import Station from '../Station'

export const getRoleId = d =>
  (d || '').indexOf('$$') === -1 ? d : d.substr(0, d.indexOf('$$'))

const Roles = ({
  targetInfo,
  reportNameMap,
  companyNameMap,
  stations,
  envConfig,
  ...props
}) => {
  const hasCopyAddFunctinality = useMemo(
    _ =>
      envConfig.authGodMode ||
      hasAccesPermits({
        authGodMode: envConfig.authGodMode,
        actionPermits: props.actionPermits,
        eventActions: [ScreenPermissions.USER_ROLE_COPY_NAME_ADD],
        matchAny: true,
      }),
    [props.actionPermits, envConfig.authGodMode]
  )

  const renameHydroToBreakerEvent = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: envConfig.authGodMode,
        actionPermits: props.actionPermits,
        eventActions: [ScreenPermissions.RENAME_HYDRO_TO_BREAKER_EVENT],
        matchAny: true,
      }),
    [envConfig.authGodMode, props.actionPermits]
  )

  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)

  const [data, setData] = useState([])
  const [dataMap, setDataMap] = useState({})
  const [fuzzyFilter, setFuzzyFilter] = useState('')

  const [users, setUsers] = useState([])
  const [actions, setActions] = useState([])
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  // const [unit, setUnit] = useState([])
  const [userTree, setUserTree] = useState({ visible: false })

  const setDataMapSetter = data => {
    const sd = {}
    data.forEach(dt => {
      const { id } = dt
      const oid = id.substr(
        0,
        id.indexOf('$$') === -1 ? id.length : id.indexOf('$$')
      )
      if (sd[oid]) {
        const ad = sd[oid]
        ad.push(dt)
      } else {
        sd[oid] = [dt]
      }
    })

    setDataMap(sd)
  }
  const breakerDisplayNameMap = useMemo(
    () => ({
      hydro_add_data: 'Read & Write',
      hydro_import: 'Import',
      hydro_process: 'Process Breaker Data',
      hydro_view: 'Read',
    }),
    []
  )
  const filterActions = useCallback(
    data => {
      if (!renameHydroToBreakerEvent) return data
      const hydroActionIds = Object.keys(breakerDisplayNameMap)
      return data.map(action => {
        if (hydroActionIds.includes(action.id) && action.category === 'Hydro') {
          return {
            ...action,
            category: 'Breaker Event',
            displayName: breakerDisplayNameMap[action.id] || action.displayName,
          }
        }
        return action
      })
    },
    [breakerDisplayNameMap, renameHydroToBreakerEvent]
  )

  useEffect(() => {
    setSpinning(true)
    List([
      getAllActions(targetInfo),
      getUsers(targetInfo)(),
      // getAllUnitInfo(targetInfo),
      getRoles(targetInfo)(),
    ])
      .traverse(Task.of, _ => _)
      .fork(setError, data => {
        setSpinning(false)
        setActions(filterActions(data.get(0)))
        setUsers(data.get(1))
        // setUnit(data.get(2))
        const retData = data.get(2)
        setData(retData)
        setDataMapSetter(retData)
      })
  }, [setSpinning, setError, targetInfo, filterActions])

  const retrieveData = useCallback(
    _ => {
      setSpinning(true)
      getRoles(targetInfo)().fork(setError, data => {
        setSpinning(false)
        setData(data)
        setDataMapSetter(data)
      })
    },
    [setError, setSpinning, targetInfo]
  )

  const deleteData = useCallback(
    id => {
      setSpinning(true)
      deleteRole(targetInfo)({ id: dataMap[id].map(i => i.id) }).fork(
        setError,
        _ => {
          setSpinning(false)
          message.info('Data Deleted.')
          retrieveData()
        }
      )
    },
    [dataMap, retrieveData, setError, setSpinning, targetInfo]
  )
  const [showEditModal, setShowEditModal] = useState(false)
  const hideEditModal = _ => setShowEditModal(false)

  const getModalInfo = useCallback(
    (id, isNew = false) => {
      const dataList = dataMap[id] || []
      const oldUsers = R.compose(
        R.uniq,
        R.flatten,
        R.map(d => d.users)
      )(dataList)

      const resCom = R.uniq(
        R.flatten(dataList.map(d => (d.resources || {}).company || []))
      )
      const resSta = R.uniq(
        R.flatten(dataList.map(d => (d.resources || {}).station || []))
      )
      const resRep = R.uniq(
        R.flatten(dataList.map(d => (d.resources || {}).report || []))
      )
      let modalInfo = dataList[0] || {}
      modalInfo = {
        ...modalInfo,
        resources: { company: resCom, station: resSta, report: resRep },
        isNew,
        users: isNew ? [] : oldUsers, // will add user in role when save
      }
      return modalInfo
    },
    [dataMap]
  )
  const renderEditModal = useCallback(
    id => {
      const modalInfo = getModalInfo(id)
      setShowEditModal({ ...modalInfo, isOld: true })
    },
    [getModalInfo]
  )

  const renderNewModal = useCallback(
    _ => {
      const modalInfo = {
        ...getModalInfo('', true),
        toggleable: true,
        isNew: true,
        users: [],
      }
      setShowEditModal(modalInfo)
    },
    [getModalInfo]
  )

  const renderDuplicateModal = useCallback(
    id => {
      const { users, ...rem } = getModalInfo(id, true)
      const modalInfo = {
        ...rem,
        users: [],
        toggleable: true,
        isNew: true,
        id: '',
        displayName: '',
        description: '',
      }
      setShowEditModal(modalInfo)
    },
    [getModalInfo]
  )

  const updateTask = roleData => {
    const { shifts, id, resources, isNew, toggleable, displayName } = roleData
    const resData = shifts === 'COMPANY' ? resources.company : resources.station
    const saveData = toggleable
      ? resData.map(d => {
          let nid = isNew
            ? id + '$$' + d
            : id.substr(
                0,
                id.indexOf('$$') === -1 ? id.length : id.indexOf('$$')
              ) +
              '$$' +
              d
          nid = nid.replace(' ', '')
          const res = {
            ...resources,
            company: shifts === 'COMPANY' ? [d] : [],
            station: shifts !== 'COMPANY' ? [d] : [],
          }
          return {
            ...roleData,
            id: nid.replaceAll(' ', '').replaceAll("'", ''),
            resources: res,
            description: displayName || '',
            users: isNew
              ? []
              : (data.find(d => d.id === nid) || {}).users || [],
          }
        })
      : [
          {
            ...roleData,
            users: (data.find(d => d.id === roleData.id) || {}).users || [],
          },
        ]
    setSpinning(true)
    saveRole(targetInfo)(saveData).fork(setError, d => {
      hideEditModal(false)
      // setSpinning(false)
      message.info('Data Saved')

      retrieveData()
    })

    // List(
    // )
    //   .traverse(Task.of, _ => _)
    //   .fork(setError, d => {
    //     message.info('Data Saved')
    //     hideEditModal(false)
    //     retrieveData()
    //   })
  }

  const getUserName = useCallback(
    user => {
      const userList = user
        .map(u => users.find(us => us.userName === u))
        .filter(u => !!u)

      return userList.map(usr => (
        <div key={usr}>
          {usr.firstName != null
            ? (usr.firstName || '') + ' ' + (usr.lastName || '')
            : usr.userName}
        </div>
      ))
    },
    [users]
  )

  const getPopoverData = (dataType, roleList, dataTypeMap) => {
    let lsData = R.uniq(
      R.flatten(roleList.map(r => (r.resources || {})[dataType] || ''))
    )

    lsData = lsData.map(i => dataTypeMap[i] || i)
    lsData = R.sort((a, b) => a.localeCompare(b), lsData)
    return lsData.map(d => <div key={d}>{d}</div>)
  }

  const stationNameMap = useMemo(
    _ =>
      R.reduce(
        (a, b) => ({ ...a, ...b }),
        {},
        stations.map(s => ({ [s.station]: s.stationName }))
      ),
    [stations]
  )

  const renderRow = useCallback(
    id => {
      const roles = dataMap[id]
      if (roles === undefined) return
      const role = roles[0]
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              '50px 300px 100px 100px 150px 100px 100px 100px 100px',
            gap: '5px',
            borderBottom: '1px solid  #eee',
            padding: '2px',
            paddingBottom: '5px',
            paddingTop: '5px',
            '&:hover': {
              backgroundColor: '#f2f2f2',
            },
          }}
        >
          <Avatar
            style={{
              marginRight: '30px',
              backgroundColor: '#fde3cf',
              color: 'red',
            }}
          >
            {role.shifts === 'COMPANY' ? 'C' : 'S'}
          </Avatar>
          <RowTitle css={{ minWidth: '200px' }}>{role.displayName}</RowTitle>

          {/* <div /> */}
          <Popover
            content={
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                {getPopoverData('report', roles, reportNameMap)}
              </div>
            }
            title="Report"
          >
            <RowTitle>Report</RowTitle>
          </Popover>
          {role.shifts === 'COMPANY' && (
            <Popover
              content={
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {getPopoverData('company', roles, companyNameMap)}
                </div>
              }
              title="Company"
            >
              <RowTitle>Company</RowTitle>
            </Popover>
          )}
          {role.shifts === 'STATION' && (
            <Popover
              content={
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {getPopoverData('station', roles, stationNameMap)}
                </div>
              }
              title="Station"
            >
              <RowTitle>Station</RowTitle>
            </Popover>
          )}
          <Button
            onClick={_ =>
              setUserTree({
                visible: true,
                data: roles,
                shifts: role.shifts,
                defaultExpandAll: true,
              })
            }
          >
            View User Role
          </Button>
          <Button type="primary" icon="edit" onClick={_ => renderEditModal(id)}>
            Update
          </Button>
          <Button
            onClick={_ =>
              setShowRenameDialog({
                id: id,
                visible: true,
              })
            }
          >
            Rename
          </Button>
          <Button onClick={_ => renderDuplicateModal(id)}>Duplicate</Button>
          <Popconfirm onConfirm={_ => deleteData(id)} title="Are you sure?">
            <Button type="danger">Delete</Button>
          </Popconfirm>
        </div>
      )
    },
    [
      companyNameMap,
      dataMap,
      deleteData,
      renderDuplicateModal,
      renderEditModal,
      reportNameMap,
      stationNameMap,
    ]
  )

  const renderRowsList = ({ height, width }) => {
    // const wd = (data || []).filter(d => (d.id || '').indexOf('$$') === -1).map(i=>i.id)
    let ids = R.uniq(data.map(d => d.id).map(getRoleId))

    ids = R.sort((a, b) => (a || '').localeCompare(b || ''), ids || [])

    ids = ids.filter(s =>
      fuzzyFilter && fuzzyFilter.trim().length > 0
        ? R.toLower(
            s + ((dataMap[s] || [])[0] || {}).displayName || ''
          ).indexOf(R.toLower(fuzzyFilter.trim())) >= 0
        : true
    )
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          //          gridGap: '5px',
          overflow: 'auto',
          height,
          width,
          borderTop: '1px solid  #eee',
          alignContent: 'baseLine',
          padding: '10px',
        }}
      >
        {ids.map(i => renderRow(i))}
      </div>
    )
  }

  const setUserTreeDialogClose = _ => setUserTree({ visible: false })
  const setUserTreeDialogVisible = shifts =>
    setUserTree({ visible: true, shifts })

  return (
    <div
      css={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      {showEditModal && (
        <RoleModal
          updateTask={updateTask}
          createTask={updateTask}
          hide={hideEditModal}
          role={showEditModal}
          users={users}
          actions={actions}
          // units={unit}
          stationData={stations}
          companyData={R.compose(
            R.map(([a, b]) => ({ companyId: a, companyName: b })),
            R.toPairs
          )(companyNameMap)}
          reportNameMap={reportNameMap}
          setSpinning={setSpinning}
          setError={setError}
          spinning={spinning}
          list={data}
          hasCopyAddFunctinality={hasCopyAddFunctinality}
        />
      )}
      {showRenameDialog && (
        <RenameModal
          targetInfo={targetInfo}
          visible={showRenameDialog ? showRenameDialog.visible : false}
          id={showRenameDialog ? showRenameDialog.id : false}
          hide={reload => {
            setShowRenameDialog(false)
            if (reload) retrieveData()
          }}
          dataMap={dataMap}
        />
      )}
      <CriteriaRow>
        <Title>Roles</Title>
        <Button
          icon="database"
          loading={spinning}
          onClick={retrieveData}
          css={{ marginLeft: '10px' }}
          type="primary"
        >
          Retrieve
        </Button>
        <Button
          icon="plus-circle"
          loading={spinning}
          onClick={_ => renderNewModal()}
          css={{ marginLeft: '10px' }}
          type="primary"
        >
          Insert
        </Button>
        <Button
          type="primary"
          onClick={e => setUserTreeDialogVisible()}
          css={{ marginLeft: '10px' }}
        >
          View Users By Role Company
        </Button>
        <Button
          type="primary"
          onClick={e => setUserTreeDialogVisible('STATION')}
          css={{ marginLeft: '10px' }}
        >
          View Users By Role Station
        </Button>

        {envConfig.transformUserRole && (
          <Popconfirm
            okText="Yes"
            cancelText="No"
            placement="bottom"
            onConfirm={_ => {
              setSpinning(true)
              transformUserGroupToRole(targetInfo).fork(setError, () => {
                setSpinning(false)
                retrieveData()
              })
            }}
            title={
              <span style={{ marginLeft: '10px' }}>
                Warning: Transform will create new roles based on Users and
                UserGroups from the previous System.
                <br />
                This function should only be used ONCE during system install.
                <br />A backup of the following table should also be taken prior
                to this task: <br />
                USERS,USER_COMPANY,USER_STATION,USER_REPORT.
              </span>
            }
          >
            <Button
              style={{ marginLeft: '100px', alignSelf: 'center' }}
              type="danger"
            >
              Transform
            </Button>
          </Popconfirm>
        )}
        <Filler />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          style={{ marginLeft: '10px', marginRight: '10px', maxWidth: '200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
      </CriteriaRow>
      <FullHeightSizer heightOffset={240}>
        {({ height, width }) => (
          <Content css={{ height: height - 20, paddingTop: '10px' }}>
            {renderRowsList({ height: height - 50, width: width - 50 })}
          </Content>
        )}
      </FullHeightSizer>
      {userTree.visible && (
        <UserTree
          users={users}
          data={data}
          companyNameMap={companyNameMap}
          stationData={stations}
          dataMap={dataMap}
          {...userTree}
          setUserTreeDialogClose={setUserTreeDialogClose}
        />
      )}
    </div>
  )
}

const mapStateToProps = state => ({
  targetInfo: getApiTargetInfo(state),
  reportNameMap: getReportNameMap(state),
  companyNameMap: getCompanyNameMap(state),
  companyUtilityMap: getCompanyUtilityMap(state),
  utilityUnitMap: getUtilityUnitsMap(state),
  utilityStationMap: getUtilityStationMap(state),
  stationUtilityMap: getStationUtilityMap(state),
  // unit: getUnits(state),
  stations: getStations(state),
  unitGroups: getUnitGroups(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
})
export default connect(mapStateToProps)(Roles)
